/* eslint-disable @typescript-eslint/no-explicit-any */
import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6 Vendor-Port W3 (Wave-0 infra) — THE RATING BACKBONE (§32).
 *
 * POST /api/v1.0/rating/ is the SYNCHRONOUS external-vendor dispatch (no celery).
 * Backend network_control/rating/entry.py RatingViewSet → dynamic handler
 * integrated_parts/<part>/<manufacturer>/handle_call.py → the FastAPI vendor
 * server / Win2008. It is ALWAYS HTTP 200: ok/errors/warnings/models/notes/
 * version live in the BODY, never in the status (HTTP 400 only for a malformed
 * envelope / permission). NaN/±Inf are cured to null server-side.
 *
 * This is the shared mutation EVERY vendor selector (fan/EW/PHX/coil) dispatches
 * through useRatingDispatch (the abortable hook in useRatingDispatch.ts). W3
 * builds it; W4-W6 reuse it.
 *
 * Sibling route: GET rating/network_internal_access_controller/ (§31) — the
 * per-user vendor-access gate the FAN selector reads once before offering
 * manufacturers. Built here now (Wave-0), consumed by the fan wave (W6).
 *
 * ★ DATA-SAFETY: postRating fires an EXTERNAL vendor call. It is NEVER invoked
 * on mount/open/browse — only from a userArmed Rate click inside the selector.
 * The read-only SMOKE must produce 0 /rating/ calls.
 */

/** The §32 envelope body. `type` is omitted for erwheel/phx; `function_name`
 * only for rosenberg/comefri/greenheck/ebm_papst/ziehl_abegg fans. */
export interface RatingEnvelope {
  part: string;
  manufacturer: string;
  type?: string;
  function_name?: string;
  input: Record<string, unknown>;
  construction_class_id?: number;
}

/** The §32 always-200 response body. */
export interface RatingResponse {
  ok?: boolean;
  models?: Array<Record<string, unknown>>;
  errors?: string[];
  warnings?: string[];
  notes?: unknown;
  version?: unknown;
  /** legacy `incoming_data['Error occured']` flag (sic). */
  'Error occured'?: boolean;
  /** the abort path resolves `{cancelled:true}` (legacy data_exchanger). */
  cancelled?: boolean;
  [key: string]: unknown;
}

/** A row from rating/network_internal_access_controller/ (per-user access). */
export interface NetworkInternalAccessRow {
  [key: string]: unknown;
}

export const ratingApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * THE shared vendor dispatch (§32). Always-200; the selector reads ok/
     * errors/warnings/models from the body. useRatingDispatch wraps this with
     * cancel-on-resubmit (keeps the last promise and .abort()s it before
     * re-firing — the legacy self.rate_request.abort()).
     */
    postRating: builder.mutation<RatingResponse, RatingEnvelope>({
      query: (envelope) => ({
        url: `/api/v1.0/rating/`,
        method: 'POST',
        body: envelope,
      }),
      // never invalidate / never cached: each rate is a fresh vendor round-trip.
    }),

    /**
     * GET rating/network_internal_access_controller/ (§31 — fan's
     * available_rating gate). Built now; the fan wave reads it once to gate the
     * manufacturer list. URL-keyed, session-cached (the legacy fetches once).
     */
    getNetworkInternalAccess: builder.query<NetworkInternalAccessRow[], void>({
      query: () => `/api/v1.0/rating/network_internal_access_controller/`,
      transformResponse: (res: unknown): NetworkInternalAccessRow[] =>
        Array.isArray(res)
          ? (res as NetworkInternalAccessRow[])
          : (((res as any)?.results as NetworkInternalAccessRow[]) ?? []),
      keepUnusedDataFor: 24 * 60 * 60,
    }),
  }),
  overrideExisting: false,
});

export const { usePostRatingMutation, useGetNetworkInternalAccessQuery } =
  ratingApi;
